import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/errors';

/**
 * Builds a React Query client with the app's caching and retry policy.
 *
 * Lives outside the provider component so tests can construct an isolated client
 * (and so the provider file only exports a component, which is what React Fast
 * Refresh needs to work reliably).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // The API client already retries transient network failures, so this
        // layer only needs to cover render-time races. Never retry a 4xx: a 401
        // or 404 will fail identically every time and just delays the error UI.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 1;
        },
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        // Refetching on every window focus is a lot of traffic for data that
        // rarely changes mid-session; reconnect is the case that matters.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
